import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useMultiStepForm } from '../../../ui-components/base/MultiStepForm';
import { fetchMarketLanguages } from '../../../redux/actions/commonDataActions';
import { setNotificationData } from '../../../redux/actions/uiActions';
import BannerService from '../../../service/BannerService';
import AddTranslationDrawer from './AddTranslationDrawer';
import styles from './TranslationStep.module.scss';
import editIcon from '../../../styles/images/edit-black.svg'
import deleteIcon from '../../../styles/images/delete-black.svg'
import noTranslationIcon from '../../../styles/images/no_translation_icon.svg'

const TranslationStep = ({ bannerId, isEditMode = false }) => {
  const { stepData, updateStepData, setStepValid, isStepValid, allStepData } = useMultiStepForm();
  const dispatch = useDispatch();
  const history = useHistory();

  // Get market languages from Redux store
  const marketLanguagesData = useSelector((state) => state.commonData?.marketLanguages);
  const marketLanguagesLoader = useSelector((state) => state.commonData?.marketLanguagesLoader);

  // Get banner type from step 0 (Banner Information)
  const bannerInformationData = allStepData[0] || {};
  const isButtonLinkMode = bannerInformationData.bannerType === 'button';

  const [translations, setTranslations] = useState(stepData.translations || {});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [deletingLocale, setDeletingLocale] = useState(null);

  // Locales that already exist on the server (loaded when editing an existing banner).
  // Only these should call the unlocalize API on delete; locales added in this session
  // are drafts with no server-side data and must be removed locally only.
  const persistedLocalesRef = useRef(new Set(Object.keys(stepData.translations || {})));

  // Fetch market languages if not already loaded
  useEffect(() => {
    const hasValidData = marketLanguagesData
      && typeof marketLanguagesData === 'object'
      && marketLanguagesData.marketDefaultLocale;

    if (!hasValidData && !marketLanguagesLoader) {
      dispatch(fetchMarketLanguages(history, 'false'));
    }
  }, [dispatch, history, marketLanguagesData, marketLanguagesLoader]);

  // Auto-populate primary locale translation from BannerInformationStep
  useEffect(() => {
    if (!isInitialized && marketLanguagesData?.marketDefaultLocale) {
      const primaryLocaleCode = marketLanguagesData.marketDefaultLocale.locale;

      // Only initialize if primary translation doesn't exist
      if (!translations[primaryLocaleCode]) {
        const primaryTranslation = {
          locale: primaryLocaleCode,
          title: bannerInformationData.title || '',
          description: bannerInformationData.description || '',
          primaryButtonText: bannerInformationData.primaryButtonText || '',
          secondaryButtonText: bannerInformationData.secondaryButtonText || '',
          link: bannerInformationData.link || '',
          imageDescription: bannerInformationData.imageDescription || '',
        };

        setTranslations(prev => ({
          ...prev,
          [primaryLocaleCode]: primaryTranslation,
        }));
      }

      setIsInitialized(true);
    }
  }, [marketLanguagesData, bannerInformationData, isInitialized]);

  const hasTitleInBannerInfo = !!(bannerInformationData.title && bannerInformationData.title.trim().length > 0);
  // Check if description was provided in Banner Information step
  const hasDescriptionInBannerInfo = !!(bannerInformationData.description && bannerInformationData.description.trim().length > 0);
  // Image Link banners with no title and no description have no translatable content
  const isNoTranslationNeeded = !isButtonLinkMode && !hasTitleInBannerInfo && !hasDescriptionInBannerInfo;
  // Check if secondary button text was provided in Banner Information step
  const hasSecondaryButtonTextInBannerInfo = !!(bannerInformationData.secondaryButtonText && bannerInformationData.secondaryButtonText.trim().length > 0);

  // Get all locales (primary + secondary languages that have translations)
  const primaryLocale = marketLanguagesData?.marketDefaultLocale;
  // Every secondary translation is validated, so every one must be shown (and editable);
  // a locale missing from otherLocales falls back to its code instead of being hidden,
  // otherwise it could silently keep the step invalid with no card or alert to fix it.
  const secondaryLocales = Object.keys(translations)
    .filter(locale => locale !== primaryLocale?.locale)
    .map(locale => (
      marketLanguagesData?.otherLocales?.find(l => l.locale === locale)
      || { locale, title: locale, primaryLanguage: false }
    ));

  const allDisplayLocales = primaryLocale ? [primaryLocale, ...secondaryLocales] : [];

  // Missing required fields per secondary translation. A translation can become incomplete
  // after it was saved, e.g. when banner_type is switched to button_link it now needs button text.
  const getMissingFields = (trans = {}) => {
    const missing = [];
    if (hasTitleInBannerInfo && (!trans.title || !trans.title.trim())) missing.push('Title');
    if (isButtonLinkMode && (!trans.primaryButtonText || !trans.primaryButtonText.trim())) {
      missing.push('Primary Button Text');
    }
    if (!trans.imageDescription || !trans.imageDescription.trim()) missing.push('Image Description');
    return missing;
  };

  const incompleteLocales = isNoTranslationNeeded ? [] : secondaryLocales.filter(
    (locale) => getMissingFields(translations[locale.locale]).length > 0
  );

  // Validate against the current banner type on every render, so the result always reflects the
  // latest Banner Information values (e.g. banner_type switched to button_link) and translations
  const computeIsStepValid = () => {
    if (!marketLanguagesData || !primaryLocale) return false;

    // Must have at least the primary locale translation
    if (!translations[primaryLocale.locale]) return false;

    // Nothing to translate: the step is satisfied without any secondary translation
    if (isNoTranslationNeeded) return true;

    // Primary locale content comes from (and is validated by) the Banner Information step
    return Object.keys(translations)
      .filter((locale) => locale !== primaryLocale.locale)
      .every((locale) => {
        const trans = translations[locale] || {};
        if (getMissingFields(trans).length > 0) return false;
        return trans.imageDescription.length <= 100;
      });
  };
  const isTranslationStepValid = computeIsStepValid();

  // Keep the wizard's validity in sync. Compared against the wizard's own value (not just on change)
  // because MultiStepForm can reset every step's validity to false (e.g. when its steps prop is
  // recreated after a refetch in edit mode), which would otherwise leave Next stuck disabled.
  useEffect(() => {
    if (isStepValid !== isTranslationStepValid) {
      setStepValid(isTranslationStepValid);
    }
  }, [isStepValid, isTranslationStepValid]);

  // Push translations up so the other steps and the submit payload see the latest values
  useEffect(() => {
    if (!marketLanguagesData || !primaryLocale || !translations[primaryLocale.locale]) return;
    updateStepData({ translations });
  }, [translations, marketLanguagesData, primaryLocale]);

  // Handlers
  const handleAddLanguage = () => {
    setEditingTranslation(null);
    setIsDrawerOpen(true);
  };

  const handleEditTranslation = (locale) => {
    setEditingTranslation({ locale, ...translations[locale] });
    setIsDrawerOpen(true);
  };

  const removeTranslationLocally = (locale) => {
    setTranslations((prev) => {
      const newTranslations = { ...prev };
      delete newTranslations[locale];
      return newTranslations;
    });
  };

  const handleDeleteTranslation = async (locale) => {
    // Create mode, or a locale added in this session (never persisted on the server):
    // remove locally only, no API call.
    if (!isEditMode || !bannerId || !persistedLocalesRef.current.has(locale)) {
      removeTranslationLocally(locale);
      persistedLocalesRef.current.delete(locale);
      return;
    }

    // Guard against duplicate requests
    if (deletingLocale) return;

    setDeletingLocale(locale);
    try {
      const response = await BannerService.unlocalizeTranslation(bannerId, locale);

      // Treat 200 as success even if already unlocalized upstream (idempotent)
      if (response.status === 200) {
        removeTranslationLocally(locale);
        persistedLocalesRef.current.delete(locale);
        dispatch(setNotificationData({
          show: true,
          type: 'SUCCESS',
          title: 'Translation Removed',
          description: 'The translation was removed successfully.',
        }));
      }
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.message;

      // 404 means the translation no longer exists on the server; remove it locally.
      if (status === 404) {
        removeTranslationLocally(locale);
        persistedLocalesRef.current.delete(locale);
        return;
      }

      let errorMessage = 'Failed to remove translation. Please try again.';

      if (status === 403) {
        errorMessage = 'Primary translations cannot be unlocalized.';
      } else if (Array.isArray(message)) {
        errorMessage = message.join('; ');
      } else if (typeof message === 'string' && message) {
        errorMessage = message;
      }

      // Retain local state on failure so the user doesn't lose sight of their data
      dispatch(setNotificationData({
        show: true,
        type: 'WARNING',
        title: 'Failed to Remove Translation',
        description: errorMessage,
      }));
    } finally {
      setDeletingLocale(null);
    }
  };

  const handleSaveTranslation = (translationData) => {
    setTranslations(prev => ({
      ...prev,
      [translationData.locale]: translationData
    }));
    setIsDrawerOpen(false);
    setEditingTranslation(null);
  };

  // Get already used locales (to exclude from dropdown)
  const usedLocales = Object.keys(translations);

  // Loading state - only show if we don't have valid data yet
  const hasValidData = marketLanguagesData?.marketDefaultLocale && marketLanguagesData?.otherLocales;

  if (!hasValidData) {
    return (
      <div className={styles.loadingState}>
        <p>Loading market languages...</p>
      </div>
    );
  }

  // No locales available (shouldn't happen if hasValidData is true)
  if (allDisplayLocales.length === 0 && Object.keys(translations).length === 0) {
    return (
      <div className={styles.emptyState}>
        No languages available for translation.
      </div>
    );
  }

  return (
    <div className={styles.translationStep}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>Translation</h2>
          <p className={styles.description}>
            Add multiple language support to this Banner by adding a translation for the Title, Description and Button Text (if applicable).
          </p>
        </div>
        <button
          className={styles.addLanguageBtn}
          type="button"
          onClick={handleAddLanguage}
          disabled={isNoTranslationNeeded}
        >
          <span>Add Language</span>
          <span>+</span>
        </button>
      </div>

      {isNoTranslationNeeded ? (
        <div className={styles.noTranslationState}>
          <img src={noTranslationIcon} alt="" className={styles.noTranslationIcon} />
          <p className={styles.noTranslationTitle}>No Translation needed</p>
          <p className={styles.noTranslationText}>No Content is added.</p>
        </div>
      ) : (
      <>
      {incompleteLocales.length > 0 && (
        <div className={styles.validationAlert} role="alert">
          Complete the required fields for {incompleteLocales.map((l) => l.title).join(', ')} to continue.
          Use the Edit icon on the language card to update the translation.
        </div>
      )}

      {/* Language Cards */}
      <div className={styles.cardsContainer}>
        {allDisplayLocales.map((locale) => {
          if (!locale) return null;

          const isPrimary = locale.primaryLanguage;
          const missingFields = isPrimary ? [] : getMissingFields(translations[locale.locale]);

          // For primary locale, always show data from BannerInformationStep
          // For secondary locales, use translation data
          const trans = isPrimary
            ? {
                title: bannerInformationData.title || '',
                description: bannerInformationData.description || '',
                primaryButtonText: bannerInformationData.primaryButtonText || '',
                secondaryButtonText: bannerInformationData.secondaryButtonText || '',
                link: bannerInformationData.link || '',
                imageDescription: bannerInformationData.imageDescription || '',
              }
            : translations[locale.locale] || {
                title: '',
                description: '',
                primaryButtonText: '',
                secondaryButtonText: '',
                link: '',
                imageDescription: '',
              };

          return (
            <div
              key={locale.locale}
              className={`${styles.languageCard} ${missingFields.length > 0 ? styles.languageCardInvalid : ''}`}
            >
              {/* Card Header */}
              <div className={styles.cardHeader}>
                <div className={styles.cardHeaderLeft}>
                  <span className={styles.languageTag}>{locale.title}</span>
                </div>
                <div className={styles.cardHeaderRight}>
                  {isPrimary && (
                    <div className={styles.primaryBadge}>
                      <span>★</span>
                      <span>Primary</span>
                    </div>
                  )}
                  {!isPrimary && (
                    <div className={styles.iconGroup}>
                      <button
                        className={styles.iconBtn}
                        type="button"
                        title="Edit"
                        onClick={() => handleEditTranslation(locale.locale)}
                      >
                        <img src={editIcon} alt="Edit Icon"/>
                      </button>
                      <div className={styles.divider} />
                      <button
                        className={styles.iconBtn}
                        type="button"
                        title="Delete"
                        disabled={deletingLocale === locale.locale}
                        onClick={() => handleDeleteTranslation(locale.locale)}
                      >
                        {deletingLocale === locale.locale ? (
                          <span className={styles.deleteSpinner} />
                        ) : (
                          <img src={deleteIcon} alt="Delete Icon"/>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Card Content */}
              <div className={styles.cardContent}>
                <p className={styles.titleText}>{trans.title || 'No title provided'}</p>
                {trans.description && (
                  <p className={styles.descriptionText}>
                    {trans.description}
                  </p>
                )}

                {/* {!isButtonLinkMode && trans.imageDescription && (
                  <p className={styles.descriptionText}>
                    {trans.imageDescription}
                  </p>
                )} */}

                {/* Buttons - only in Button Link mode */}
                {isButtonLinkMode && (
                  <div className={styles.buttonGroup}>
                    {trans.primaryButtonText && (
                      <button className={styles.primaryButton} type="button">
                        {trans.primaryButtonText}
                      </button>
                    )}
                    {trans.secondaryButtonText && (
                      <button className={styles.secondaryButton} type="button">
                        {trans.secondaryButtonText}
                      </button>
                    )}
                  </div>
                )}

                {missingFields.length > 0 && (
                  <p className={styles.cardErrorText}>
                    Required: {missingFields.join(', ')}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      </>
      )}

      {/* Add/Edit Translation Drawer */}
      <AddTranslationDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setEditingTranslation(null);
        }}
        onSave={handleSaveTranslation}
        isButtonLinkMode={isButtonLinkMode}
        hasTitleInBannerInfo={hasTitleInBannerInfo}
        hasDescriptionInBannerInfo={hasDescriptionInBannerInfo}
        hasSecondaryButtonTextInBannerInfo={hasSecondaryButtonTextInBannerInfo}
        existingTranslation={editingTranslation}
        usedLocales={usedLocales}
      />
    </div>
  );
};

export default TranslationStep;
